
import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ProductForm, ProductFormData } from '@/components/product/ProductForm';
import { BulkOperations } from '@/components/product/BulkOperations';
import { ProductsHeader } from '@/components/product/ProductsHeader';
import { ProductSearch } from '@/components/product/ProductSearch';
import { ProductsTabContent } from '@/components/product/ProductsTabContent';
import { DeleteConfirmationDialog } from '@/components/product/DeleteConfirmationDialog';
import { Product } from '@/components/product/ProductCard';
import { useProducts } from '@/hooks/useProducts';

const Products = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [productToEdit, setProductToEdit] = useState<ProductFormData | undefined>();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  const {
    filteredProducts,
    categories,
    loading,
    searchQuery,
    setSearchQuery,
    isDuplicating,
    selectedProduct,
    isDeleting,
    fetchProducts,
    duplicateProduct,
    addToProducts,
    confirmDelete,
    cancelDelete,
    deleteProduct,
    // Bulk operations
    isSelectionMode,
    selectedProducts,
    toggleSelectionMode,
    selectProduct,
    selectAll,
    clearSelection,
    updateCategoryBulk,
    deleteBulk,
    tagAllProducts
  } = useProducts();

  const handleAddNew = () => {
    setProductToEdit(undefined);
    setIsFormOpen(true);
  };
  
  const handleEdit = (product: Product) => {
    setProductToEdit({
      id: product.id,
      name: product.name,
      description: product.description,
      price: product.price,
      currency: product.currency,
      category: product.category,
      image_url: product.image_url,
      affiliate_link: product.affiliate_link,
      source: product.source
    });
    setIsFormOpen(true);
  };

  const handleDelete = (id: string, source: 'products' | 'recommendations') => {
    if (confirmDelete(id, source)) {
      setIsDeleteDialogOpen(true);
    }
  };

  // Get products for the active tab to enable "Select All" functionality
  const getActiveTabProducts = () => {
    if (activeTab === 'all') {
      return filteredProducts;
    } else if (activeTab === 'permanent') {
      return filteredProducts.filter(p => p.source === 'products');
    } else if (activeTab === 'recommendations') {
      return filteredProducts.filter(p => p.source === 'recommendations');
    }
    return [];
  };

  const handleSelectAll = () => {
    const productsToSelect = getActiveTabProducts();
    selectAll(productsToSelect || []);
  };

  return (
    <div className="space-y-6">
      <ProductsHeader 
        isSelectionMode={isSelectionMode}
        toggleSelectionMode={toggleSelectionMode}
        onAddNew={handleAddNew}
      />

      {isSelectionMode && (
        <BulkOperations 
          selectedProducts={selectedProducts}
          onUpdateCategory={updateCategoryBulk}
          onDeleteProducts={deleteBulk}
          onClearSelection={clearSelection}
          onSelectAll={handleSelectAll}
          onTagAll={tagAllProducts}
          availableProducts={getActiveTabProducts()}
        />
      )}

      <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
          <TabsList className="h-auto justify-start">
            <TabsTrigger value="all">All Products</TabsTrigger>
            <TabsTrigger value="permanent">Permanent Products</TabsTrigger>
            <TabsTrigger value="recommendations">List Recommendations</TabsTrigger>
            <TabsTrigger value="categories">Categories</TabsTrigger>
          </TabsList>
          
          <ProductSearch 
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            isSelectionMode={isSelectionMode}
            toggleSelectionMode={toggleSelectionMode}
          />
        </div>
        
        <ProductsTabContent 
          activeTab={activeTab}
          filteredProducts={filteredProducts}
          categories={categories}
          loading={loading}
          searchQuery={searchQuery}
          isDuplicating={isDuplicating}
          selectedProductId={selectedProduct?.id}
          isSelectionMode={isSelectionMode}
          selectedProducts={selectedProducts}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onDuplicate={duplicateProduct}
          onAddToProducts={addToProducts}
          onAddNew={handleAddNew}
          onSelectProduct={selectProduct}
        />
      </Tabs>
      
      <ProductForm
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        initialData={productToEdit}
        onSave={fetchProducts}
      />
      
      <DeleteConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onCancel={cancelDelete}
        onConfirm={deleteProduct}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default Products;
